import { pieArcLabelClasses, PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

interface UsageData {
  totalCredits: number;
  remainingCredits: number;
  creditsUsed: number;
}

const MonthlySpendPieChart = ({ usageData }: { usageData: UsageData }) => {
  const StyledText = styled('text')(({ theme }) => ({
    fill: theme.palette.text.primary,
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fontSize: 16,
    fontWeight: 'bold',
  }));

  function PieCenterLabel({ children }: { children: React.ReactNode }) {
    const { width, height, left, top } = useDrawingArea();
    return (
      <StyledText x={left + width / 2} y={top + height / 2}>
        {children}
      </StyledText>
    );
  }

  // Prepare chart data
  const chartData = [
    {
      name: 'Credits Remaining',
      value: usageData.remainingCredits || 0,
      color: '#2196F3',
    },
    {
      name: 'Credits Used',
      value: usageData.creditsUsed || 0,
      color: '#FF9800',
    },
  ].filter(item => item.value > 0); // Only show items with usage

  const totalUsage = chartData.reduce((acc, curr) => acc + curr.value, 0);

  return (
    <Box sx={{ position: 'relative' }}>
      <PieChart
        series={[
          {
            data: chartData.map((item, index) => ({
              id: index,
              value: item.value,
              label: item.name,
              color: item.color,
            })),
            highlightScope: { fade: 'global', highlight: 'item' },
            arcLabel: item => `${Math.round(item.value)}`,
            type: 'pie',
            innerRadius: 70,
          },
        ]}
        width={300}
        height={250}
        sx={{
          [`& .${pieArcLabelClasses.root}`]: {
            fontWeight: 'bold',
            fill: '#ffffff',
            fontSize: 14,
          },
        }}
        margin={{ top: 0, right: 0, bottom: 40, left: 0 }}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
            padding: 0,
            itemGap: 20,
            itemMarkHeight: 10,
            itemMarkWidth: 10,
            labelStyle: {
              fontSize: 10,
              fontWeight: '500',
              letterSpacing: '0.25px',
            },
          },
        }}
      >
        <PieCenterLabel>Total Usage</PieCenterLabel>
      </PieChart>
      {totalUsage === 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            No usage data
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MonthlySpendPieChart;
